/**
 * «Preparar el envío», al pie de Mensajes (fase 4): con los mensajes guardados y listos, arma el padrón del envío (una
 * fila por persona de cada grupo que va) y, para los grupos de WhatsApp, la campaña de Activa que WhatsApp oficial
 * ofrece en su Audiencia, con el grupo de control afuera (nunca tiene contacto), el techo de Descuentos y sólo quien
 * puede recibir. Dice cuántos quedaron, por qué no los demás y lleva al asistente del oficial con esa campaña ya
 * elegida; los grupos de mail salen por Snov. Mientras no salió nada, se puede preparar de nuevo (después de cambiar un
 * mensaje, por ejemplo): rehace la misma campaña y el padrón.
 *
 * Apagado, dice por qué al lado del botón (no sólo al pasar el mouse); si ningún grupo va por WhatsApp, el botón dice
 * que es por mail y no promete un envío por WhatsApp. El resultado (o el error) se trae a la vista al llegar: antes caía
 * debajo del pie (auditoría de diseño del 18/10/2026).
 *
 * Lo primero que dice es cuántos tienen el sí: a los demás el oficial no les escribe. Y como la preparación espera su
 * turno si hay otra (o un «Confirmar» del oficial) en curso (`backend/src/shared/turno-pesado.util.ts`, hasta 45 s), y el
 * backend no avisa mientras espera, pasados unos segundos lo dice al lado del botón: no se queda en «Preparando…».
 *
 * Por el oficial queda una campaña por cada plantilla de Meta que eligió Mensajes, en borrador (R3, 29/10/2026): se
 * listan con sus grupos y cada una lleva al asistente. Ya preparada, también las que salieron: una puede haber salido y
 * otra seguir en borrador, y ésa se sigue armando.
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { num, plural } from "../../campana-nueva/utils/format";
import { cuandoSubida } from "../../base-ventas/logic/base";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { rutaResultado } from "../../segmentaciones/logic/segmentar";
import { leerError, leerRespuesta, textoError } from "../../wa-oficial/logic/respuesta";
import { ESTADO_CAMPANA } from "../../wa-oficial/logic/textos";
import { enviosApi } from "../api";
import {
  RUTA_HISTORIAL_CARGAS,
  gruposRepetidos,
  rutaArmarEnOficial,
  rutaCampanaOficial,
  textoAfuera,
  textoCampanaOficial,
  textoControlContaminado,
  textoOferta,
  textoPadron,
} from "../logic/envio";
import type { CampanaOficialDelEnvio, EstadoEnvio, GrupoPorMail, Preparacion } from "../types";
import { SnovDelEnvio } from "./SnovDelEnvio";

/** Pasado esto, «Preparando…» explica que puede estar esperando su turno. */
export const AVISO_TURNO_MS = 4_000;
export const TEXTO_ESPERA_TURNO = "Sigue preparando: si hay otra preparación en curso, espera su turno (hasta 45 segundos).";

export function PrepararEnvio({
  segmentacionId,
  listo,
  sinGuardar,
  porWhatsapp,
}: {
  segmentacionId: string;
  listo: boolean;
  sinGuardar: boolean;
  /** cuántos grupos que van salen por WhatsApp: sin ninguno, el envío es el padrón y Snov (el botón dice «por mail») */
  porWhatsapp: number;
}) {
  // undefined = todavía no se sabe si ya estaba preparada
  const [estado, setEstado] = useState<EstadoEnvio | null | undefined>(undefined);
  const [prep, setPrep] = useState<Preparacion | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [demora, setDemora] = useState(false);
  const [error, setError] = useState("");
  const resultado = useRef<HTMLDivElement>(null);
  // al vincular una campaña de Snov se vuelve a leer el estado: ya trae quién la recibió
  const [vuelta, setVuelta] = useState(0);
  const releer = () => setVuelta((n) => n + 1);

  useEffect(() => {
    let vivo = true;
    enviosApi
      .ver(segmentacionId)
      .then((r) => {
        const l = leerRespuesta<EstadoEnvio | null>(r);
        if (!vivo) return;
        // si no se pudo saber, se puede preparar igual: el backend frena lo que ya salió (YA_SALIO)
        setEstado(l.ok ? (l.data ?? null) : null);
        // al volver a leer, lo recién preparado deja lugar a cómo quedó; recién ahora, así lo de Snov no se desarma
        if (vuelta > 0 && l.ok && l.data) setPrep(null);
      })
      .catch(() => vivo && setEstado(null));
    return () => {
      vivo = false;
    };
  }, [segmentacionId, vuelta]);

  // el error de un intento anterior deja de ser cierto cuando cambian los mensajes
  useEffect(() => {
    setError("");
  }, [listo, sinGuardar]);

  const yaSalio = !!estado?.yaSalio;
  // los grupos de mail: los de recién preparada o los de cómo quedó
  const deMail = prep ? prep.porMail : (estado?.porMail ?? []);
  const preparada = !!prep || !!estado;
  const motivo = sinGuardar
    ? "Guardá los mensajes primero"
    : !listo
      ? "Revisá los grupos marcados primero"
      : yaSalio
        ? "Ya salió: no se rehace"
        : "";

  // si tarda, puede estar esperando su turno: se dice, para que no parezca colgado
  useEffect(() => {
    setDemora(false);
    if (!preparando) return;
    const t = setTimeout(() => setDemora(true), AVISO_TURNO_MS);
    return () => clearTimeout(t);
  }, [preparando]);

  // lo que respondió el servidor, a la vista
  useEffect(() => {
    if (error || prep) resultado.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [error, prep]);

  const preparar = async () => {
    setPreparando(true);
    setError("");
    try {
      const l = leerRespuesta<Preparacion>(await enviosApi.preparar(segmentacionId));
      if (!l.ok) {
        setError(textoError(l));
        return;
      }
      setPrep(l.data);
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setPreparando(false);
    }
  };

  return (
    <>
      {motivo && (
        <p id={`motivo-envio-${segmentacionId}`} className="text-sm text-slate-600" data-testid="motivo-envio">
          {motivo}.
        </p>
      )}
      <button
        type="button"
        className={btnPrimario}
        disabled={!!motivo || preparando || estado === undefined}
        title={motivo || undefined}
        aria-describedby={motivo ? `motivo-envio-${segmentacionId}` : undefined}
        onClick={() => void preparar()}
      >
        {preparando ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <Send size={15} aria-hidden="true" />}
        {preparando ? "Preparando…" : preparada ? "Preparar de nuevo" : porWhatsapp === 0 ? "Preparar el envío por mail" : "Preparar el envío"}
      </button>
      {preparando && demora && (
        <p role="status" className="text-sm text-slate-600" data-testid="envio-esperando-turno">
          {TEXTO_ESPERA_TURNO}
        </p>
      )}
      <div ref={resultado} className="w-full space-y-3 empty:hidden">
        {error && (
          <p role="alert" className="w-full rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
        {prep ? <Resumen p={prep} /> : estado ? <YaPreparada e={estado} segmentacionId={segmentacionId} /> : null}
        {deMail.length > 0 && <SnovDelEnvio segmentacionId={segmentacionId} grupos={deMail.map((g) => ({ id: g.id, nombre: g.nombre }))} alVincular={releer} />}
      </div>
    </>
  );
}

function Resumen({ p }: { p: Preparacion }) {
  const sinSi = p.reciben - p.conSi;
  const porMail = p.porMail.reduce((n, g) => n + (g.reciben ?? 0), 0);
  return (
    <div className="w-full space-y-3 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="envio-preparado">
      {p.grupos.length > 0 ? (
        // primero los que tienen el sí: a los demás el oficial no les escribe (el aviso de abajo dice por qué)
        <p className="font-semibold text-slate-900">
          Quedó lista para WhatsApp oficial: {num(p.conSi)} con el sí en {num(p.grupos.length)} {plural(p.grupos.length, "grupo", "grupos")}
          {sinSi > 0 && `, de ${num(p.reciben)} ${plural(p.reciben, "persona", "personas")} en la campaña`}.
        </p>
      ) : (
        <p className="font-semibold text-slate-900">
          Quedó preparada por mail: {num(porMail)} {plural(porMail, "persona", "personas")} en {num(p.porMail.length)} {plural(p.porMail.length, "grupo", "grupos")}.
        </p>
      )}
      <ul className="space-y-1.5 text-sm text-slate-700">
        {p.grupos.map((g) => {
          const afuera = textoAfuera(g.afuera);
          return (
            <li key={g.id} data-testid={`envio-grupo-${g.id}`}>
              <span className="font-semibold text-slate-900">{g.nombre}</span> · {textoOferta(g.oferta)}
              {g.cupon && (
                <>
                  {" "}
                  con <span className="font-mono font-semibold">{g.cupon}</span>
                </>
              )}
              : {num(g.conSi)} con el sí{g.conSi < g.reciben && `, de ${num(g.reciben)} en la campaña`}.
              {afuera && <span className="block text-[13px] text-slate-500">{afuera}</span>}
            </li>
          );
        })}
        {p.porMail.filter((g) => g.reciben !== undefined).map((g) => (
          <GrupoDeMail key={g.id} g={g} />
        ))}
      </ul>
      <p className="text-[13px] text-slate-600">
        El grupo de control no está en la campaña ni en el mail: no hay forma de mandarle, y contra él se mide el resultado. Cada persona queda en el padrón del envío, con la hora en que le llega.
      </p>
      {sinSi > 0 && (
        <p className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="envio-sin-si">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {num(sinSi)} no {plural(sinSi, "dijo", "dijeron")} si {plural(sinSi, "acepta", "aceptan")} recibir mensajes: el oficial no les escribe.
            {!p.consentimientoDeclarado && (
              <>
                {" "}
                Si el comercio te confirmó que aceptaron, declaralo en el{" "}
                <Link to={RUTA_HISTORIAL_CARGAS} className="font-semibold underline">
                  Historial de cargas
                </Link>{" "}
                y prepará el envío de nuevo.
              </>
            )}
          </span>
        </p>
      )}
      {p.porMail.length > 0 && (
        <p className="text-sm text-slate-600" data-testid="envio-por-mail">
          {p.porMail.map((g) => g.nombre).join(", ")} {plural(p.porMail.length, "va", "van")} por mail: sale por Snov, no por acá.
        </p>
      )}
      {p.oficiales?.length ? (
        <CampanasDelOficial campanas={p.oficiales} borradas={p.borradas ?? []} />
      ) : (
        p.campanaActivaId && (
          <Link to={rutaArmarEnOficial(p.campanaActivaId)} className={btnPrimario}>
            Armar en el oficial
          </Link>
        )
      )}
    </div>
  );
}

/**
 * Las campañas del oficial del envío, una por plantilla: con su estado, su plantilla y sus grupos, y a dónde seguir (un
 * borrador, al asistente). Un grupo en dos campañas se dice en rojo: le llegaría dos veces.
 */
function CampanasDelOficial({ campanas, borradas = [] }: { campanas: CampanaOficialDelEnvio[]; borradas?: string[] }) {
  const repetidos = gruposRepetidos(campanas);
  const borradores = campanas.filter((c) => c.estado === "borrador").length;
  return (
    <div className="space-y-2" data-testid="envio-oficiales">
      <p className="text-sm font-semibold text-slate-900">
        {campanas.length === 1
          ? "En el WhatsApp oficial:"
          : `En el WhatsApp oficial, una campaña por plantilla (${num(campanas.length)}${borradores && borradores < campanas.length ? `, ${num(borradores)} en borrador` : ""}):`}
      </p>
      <ul className="space-y-2">
        {campanas.map((c) => (
          <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`envio-oficial-${c.id}`}>
            <span className="min-w-0 flex-[1_1_240px] text-sm">
              <span className="font-semibold text-slate-900">{c.nombre}</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${ESTADO_CAMPANA[c.estado]?.cls ?? "bg-slate-100 text-slate-600"}`}>{ESTADO_CAMPANA[c.estado]?.label ?? c.estado}</span>
              <span className="block text-[13px] text-slate-600">{textoCampanaOficial(c)}</span>
            </span>
            <Link to={rutaCampanaOficial(c)} className={c.estado === "borrador" && borradores === 1 ? btnPrimario : btnSecundario}>
              {c.estado === "borrador" ? "Armar en el oficial" : "Ver la campaña"}
            </Link>
          </li>
        ))}
      </ul>
      {repetidos.length > 0 && (
        <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-semibold text-red-700" data-testid="envio-oficial-repetidos">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>
            {repetidos.join(", ")} {plural(repetidos.length, "está", "están")} en más de una campaña del oficial: si salen las dos, les llega dos veces. Prepará el envío de nuevo
            antes de mandar.
          </span>
        </p>
      )}
      {borradas.length > 0 && (
        <p className="text-[13px] text-slate-600" data-testid="envio-oficial-borradas">
          {plural(borradas.length, "Se borró el borrador", "Se borraron los borradores")} {borradas.map((n) => `«${n}»`).join(", ")}: sus grupos van en otra campaña.
        </p>
      )}
    </div>
  );
}

function GrupoDeMail({ g }: { g: GrupoPorMail }) {
  const afuera = textoAfuera(g.afuera);
  const n = g.reciben ?? 0;
  return (
    <li data-testid={`envio-mail-${g.id}`}>
      <span className="font-semibold text-slate-900">{g.nombre}</span> · {textoOferta(g.oferta ?? 0)}
      {g.cupon && (
        <>
          {" "}
          con <span className="font-mono font-semibold">{g.cupon}</span>
        </>
      )}
      : {num(n)} {plural(n, "recibe", "reciben")} por mail.
      {afuera && <span className="block text-[13px] text-slate-500">{afuera}</span>}
    </li>
  );
}

function YaPreparada({ e, segmentacionId }: { e: EstadoEnvio; segmentacionId: string }) {
  const porMail = e.porMail ?? [];
  const total = e.grupos.reduce((n, g) => n + (g.reciben || 0), 0) + porMail.reduce((n, g) => n + (g.reciben || 0), 0);
  const grupos = e.grupos.length + porMail.length;
  const contaminado = textoControlContaminado(e.padron?.controlContaminado ?? 0);
  // un backend anterior no dice qué salió: lo que salía era WhatsApp
  const salioWhatsapp = e.whatsappSalio ?? e.yaSalio;
  return (
    <div className="w-full space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" data-testid="envio-ya-preparada">
      <p>
        Preparada{e.preparadaAt ? ` el ${cuandoSubida(e.preparadaAt)}` : ""}: {num(total)} {plural(total, "persona", "personas")} en {num(grupos)} {plural(grupos, "grupo", "grupos")}.
      </p>
      {e.padron && <p data-testid="envio-padron">{textoPadron(e.padron)}</p>}
      {contaminado && (
        <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 font-semibold text-red-700" data-testid="envio-control-contaminado">
          <AlertTriangle size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{contaminado}</span>
        </p>
      )}
      {porMail.length > 0 && (
        <p data-testid="envio-ya-por-mail">
          Por mail, por Snov: {porMail.map((g) => `${g.nombre} (${num(g.enviados)} de ${num(g.reciben)} ${plural(g.reciben, "enviado", "enviados")})`).join(", ")}.
        </p>
      )}
      {e.yaSalio && (
        <p>
          Ya salió: no se rehace.
          {salioWhatsapp && e.campanaActivaId && (
            <>
              {" "}
              La seguís en{" "}
              <Link to="/activa/oficial/campanas" className="font-semibold text-violet-700 underline">
                WhatsApp oficial → Campañas
              </Link>
              .
            </>
          )}
        </p>
      )}
      {/* las del oficial, con su estado: si una ya salió, la que sigue en borrador se sigue armando (R3) */}
      {e.oficiales?.length ? <CampanasDelOficial campanas={e.oficiales} /> : null}
      <div className="flex flex-wrap gap-2">
        {/* un mail que ya mandó Snov no cierra lo de WhatsApp: mientras no salió, se sigue armando en el oficial */}
        {e.campanaActivaId && !salioWhatsapp && !e.oficiales?.length && (
          <Link to={rutaArmarEnOficial(e.campanaActivaId)} className={btnSecundario}>
            Armar en el oficial
          </Link>
        )}
        {/* con algo afuera ya se mide contra el control (fase 5) */}
        {e.yaSalio && (
          <Link to={rutaResultado(segmentacionId)} className={btnSecundario}>
            Ver el resultado
          </Link>
        )}
      </div>
    </div>
  );
}
